import React from "react";
import { cn } from "@/lib/utils";

/**
 * PooPluckers brand mark + wordmark.
 *
 * Variants:
 *   - `variant="full"` (COMPOSED LOCKUP — refactored 2026-02 dark-mode fix)
 *     Renders the Scottie dogmark PNG (with the existing `pp-dog-mark`
 *     dark-mode CSS filter applied) NEXT TO a typed-HTML wordmark using
 *     CSS variables `--pp-orange` for "Poo" and `--pp-slate` for
 *     "Pluckers". Both halves auto-flip in dark mode (silhouette via the
 *     filter, "Pluckers" via the token), while "Poo" stays orange in
 *     both modes. Mirrors the SharedNav lockup proportions.
 *
 *     Pipes the consumer's `className` to the outer `<span>` for layout
 *     control (margins, blocks, link wrappers). The wrapper's inline-flex
 *     intrinsic width is forced via inline `style={{width: "max-content"}}`
 *     so any incoming `w-32`/`w-24` width utility from legacy call sites
 *     (Login, Signup, GoogleAuthCallback — pre-refactor) is neutralized
 *     and the lockup never gets clipped horizontally. Height utilities
 *     from the consumer (e.g. `h-32`) DO apply, giving the wrapper extra
 *     vertical breathing room — the lockup itself centers vertically.
 *
 *     KNOWN TRADE-OFF (PRD backlog P3): the dog-mark's orange COLLAR
 *     goes monochrome in dark mode because the `pp-dog-mark` filter
 *     (`brightness(0) invert(0.92)`) can't preserve a single color.
 *     Matches the existing SharedNav precedent. Fix is blocked on
 *     obtaining the official vector source — see PRD §"Backlog: Restore
 *     orange collar in dark mode".
 *
 *   - `variant="lockup"` (default): mark (img) on a permanent-white pill
 *     + typed wordmark side-by-side. Used by miscellaneous embedded
 *     surfaces. Unchanged in this refactor.
 *
 *   - `variant="mark"`: just the dog icon, on a permanent-white pill.
 *     Unchanged in this refactor.
 *
 *   - `variant="wordmark"`: TYPOGRAPHY-ONLY (no image). Used by the
 *     navbar variant where a PNG mark looked fuzzy at 40px DPR.
 *     Unchanged in this refactor.
 *
 * Testid plumbing — the outer wrapper for variant="full" carries
 * `data-testid="brand-logo-full"` by default. Pass an explicit
 * `data-testid` prop to override (e.g. SharedFooter uses
 * `data-testid="shared-footer-logo"`).
 */
export default function Logo({
  variant = "lockup",
  className = "",
  markClassName = "",
  "data-testid": testIdProp,
}) {
  if (variant === "wordmark") {
    return (
      <span
        className={cn(
          "font-heading text-xl font-bold tracking-tight text-pp-text sm:text-2xl select-none",
          className
        )}
        data-testid={testIdProp || "brand-logo-wordmark"}
      >
        <span className="text-brand-orange">Poo</span>Pluckers
      </span>
    );
  }

  if (variant === "full") {
    // Composed lockup — dogmark (PNG with pp-dog-mark dark-mode filter)
    // + typed wordmark (CSS-variable colored). Canonical sizing chosen
    // to feel prominent on the auth pages (their old call site was
    // `h-32 w-32` which rendered a distorted 128×128 PNG; the composed
    // lockup at h-20 + 44px text fits naturally inside that bounding
    // box with comfortable vertical centering).
    return (
      <span
        className={cn("inline-flex items-center gap-3 select-none", className)}
        // Force width to content regardless of consumer `w-X` so the
        // wordmark never clips. `max-content` beats any class-utility
        // width because inline style wins the cascade.
        style={{ width: "max-content" }}
        data-testid={testIdProp || "brand-logo-full"}
      >
        <img
          src="/brand/poopluckers-dogmark.png"
          alt=""
          aria-hidden="true"
          draggable={false}
          className="pp-dog-mark h-20 w-auto"
        />
        <span
          style={{
            fontFamily: '"Manrope", system-ui, -apple-system, sans-serif',
            fontWeight: 800,
            fontSize: 44,
            lineHeight: 1,
            letterSpacing: "-0.02em",
          }}
        >
          <span style={{ color: "var(--pp-orange)" }}>Poo</span>
          <span style={{ color: "var(--pp-slate)" }}>Pluckers</span>
        </span>
      </span>
    );
  }

  if (variant === "mark") {
    return (
      <span
        className={cn(
          "inline-flex items-center justify-center rounded-full bg-white p-1",
          className
        )}
      >
        <img
          src="/brand/pooppluckers-mark.png"
          alt="PooPluckers"
          className={cn("select-none object-contain", markClassName || "h-7 w-7")}
          draggable={false}
          data-testid={testIdProp || "brand-logo-mark"}
        />
      </span>
    );
  }

  // lockup (mark + text). The mark sits on a permanent white pill so it stays
  // readable when the navbar is dark; the "PooPluckers" text uses pp-text so
  // it adapts (slate in light, light in dark).
  return (
    <span
      className={cn("inline-flex items-center gap-2 select-none", className)}
      data-testid={testIdProp || "brand-logo-lockup"}
    >
      <span className="inline-flex items-center justify-center rounded-full bg-white p-1">
        <img
          src="/brand/pooppluckers-mark.png"
          alt=""
          aria-hidden="true"
          className={cn("object-contain", markClassName || "h-7 w-7")}
          draggable={false}
        />
      </span>
      <span className="font-heading text-xl font-bold tracking-tight text-pp-text">
        <span className="text-brand-orange">Poo</span>Pluckers
      </span>
    </span>
  );
}
